import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom'
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '../icons';
import { Button, LoadingSpinner } from '../components';
import { API_BASE_URL, LoadingSpinnerMeta } from '../data';
import { ViewIcon } from '../icons/ViewIcon';
import { UserLogDetailModal } from '../components/UserLogDetailModal';
import type { UserLog } from '../data/types';

const ITEMS_PER_PAGE = 10

type PaginationData = {
    current_page: number
    last_page: number
    per_page: number
    total: number
}

const Capitalize = (value: string) => {
    return `${value[0].toUpperCase()}${value.slice(1)}`
}

export const UserLogs = () => {

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedLog, setSelectedLog] = useState<UserLog | null>(null);
    const [userLogs, setUserLogs] = useState<UserLog[]>([]);

    const [pagination, setPagination] = useState<PaginationData>({
        current_page: 1,
        last_page: 1,
        per_page: ITEMS_PER_PAGE,
        total: 0
    });

    const [appliedFilters, setAppliedFilters] = useState({
        name: '',
        email: '',
    });

    const hasFetched = useRef(false)

    const buildQueryString = (page: number, filters: typeof appliedFilters) => {
        const params = new URLSearchParams()
        params.append('per_page', ITEMS_PER_PAGE.toString())
        params.append('page', page.toString())
        if (filters.name) params.append('name', filters.name)
        if (filters.email) params.append('email', filters.email)
        return params.toString()
    }

    const fetchLogs = async (page: number, filters: typeof appliedFilters) => {
        setLoading(true)
        setError(null)

        try {
            const queryString = buildQueryString(page, filters)
            const response = await fetch(`${API_BASE_URL}/user-log?${queryString}`, { credentials: 'include' })
            if (!response.ok) {
                const text = await response.text()
                const textObj = JSON.parse(text)
                throw new Error(`${textObj.message}`)
            }
            const result = await response.json()
            setUserLogs(result.data || [])
            setPagination({
                current_page: result.current_page || 1,
                last_page: result.last_page || 1,
                per_page: result.per_page || ITEMS_PER_PAGE,
                total: result.total || 0
            })
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching logs')
        }
        finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true
            fetchLogs(1, appliedFilters)
        }
    }, [])

    const handleSearch = () => {
        const filters = { name: name.trim(), email: email.trim() }
        setAppliedFilters(filters)
        fetchLogs(1, filters)
    }

    const handleClear = () => {
        setName('')
        setEmail('')
        const emptyFilters = { name: '', email: '' }
        setAppliedFilters(emptyFilters)
        fetchLogs(1, emptyFilters)
    }

    const handlePageChange = (page: number) => {    
        if (page >= 1 && page <= pagination.last_page) {
            fetchLogs(page, appliedFilters)
        }
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch()
    }

    const handleViewClick = (log: UserLog) => {
        setSelectedLog(log);
        setIsDetailModalOpen(true);
    }

    const hanleClose = () => {
        setSelectedLog(null);
        setIsDetailModalOpen(false);
    }

    const formatDate = (timestamp: string) => {
        return new Date(timestamp).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    };


    return (
        <>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className='mb-4'>
                        <Link
                            to="/admin"
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-2" />
                            Back to Admin Hub
                        </Link>
                    </div>
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            User Logs
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                            Monitor detials on user activity
                        </p>
                    </div>

                    <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                            <input
                                type="text"
                                placeholder="Search by name..."
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onKeyDown={handleKeyPress}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                            <input
                                type="text"
                                placeholder="Search by email..."
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={handleKeyPress}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        {/* Search Buttons */}
                        <div className="flex gap-2">
                            <Button onClick={handleSearch} size="sm">
                                <SearchIcon className="w-4 h-4 me-2" />
                                Search
                            </Button>
                            <Button onClick={handleClear} variant="outline" size="sm">
                                Clear
                            </Button>
                        </div>

                        {error && (
                            <div className="mt-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded-lg">
                                {error}
                            </div>
                        )}

                        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                            Showing {pagination.total} log{pagination.total !== 1 ? 's' : ''}
                        </div>
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    <div className="flex justify-center">
                        {!loading && (
                            <div className="w-full">
                                <table className="min-w-full bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
                                    <thead className="bg-gray-200 dark:bg-gray-700">
                                        <tr>
                                            <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Volunteer ID</th>
                                            <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Name</th>
                                            <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Email</th>
                                            <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Action</th>
                                            <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Performed At</th>
                                            <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Entries Effected</th>
                                            <th className="py-4 px-6 text-start text-gray-700 dark:text-gray-300">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {userLogs.map((log, index) => (
                                            <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                                                <td className="px-6 text-gray-900 dark:text-gray-100">{log.user_csid}</td>
                                                <td className="px-6 text-gray-900 dark:text-gray-100">{log.name}</td>
                                                <td className="px-6 text-gray-900 dark:text-gray-100">{log.email}</td>
                                                <td className="px-6 text-gray-900 dark:text-gray-100">{Capitalize(log.action)}</td>
                                                <td className="px-6 text-gray-900 dark:text-gray-100">{formatDate(log.created_at)}</td>
                                                <td className="px-6 text-gray-900 dark:text-gray-100">{log?.detail?.csids?.length || 0}</td>
                                                <td className="px-6 text-gray-900 dark:text-gray-100">
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            onClick={() => { handleViewClick(log) }}
                                                            variant="icon"
                                                            size="sm"
                                                            title="View log details">
                                                            <ViewIcon className="w-5 h-5" /> View Details
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {pagination.last_page > 1 && (
                                    <div className="flex justify-center items-center gap-4 mt-6">
                                        <Button
                                            onClick={() => handlePageChange(pagination.current_page - 1)}
                                            disabled={pagination.current_page === 1 || loading}
                                            variant="outline"
                                            size="sm"
                                        >
                                            <ChevronLeftIcon className="w-5 h-5" />
                                        </Button>

                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                            Page {pagination.current_page} of {pagination.last_page}
                                        </span>

                                        <Button
                                            onClick={() => handlePageChange(pagination.current_page + 1)}
                                            disabled={pagination.current_page === pagination.last_page || loading}
                                            variant="outline"
                                            size="sm"
                                        >
                                            <ChevronRightIcon className="w-5 h-5" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>


                </div>
            </div>


            <UserLogDetailModal
                log={selectedLog}
                isOpen={isDetailModalOpen}
                onClose={hanleClose}
            />

        </>

    );
}
